import { useEffect, useRef } from 'react';
import type { Tv2mLinkContext } from '@apm/contracts';
import type { Tv2mSdk } from '@apm/tv2m-client-sdk';
import { useT } from '../i18n/LocaleProvider.js';

/**
 * 데모 완료 화면 — `purchase_completed` 를 보고한다 (`docs/09` T7).
 *
 * ── B10 이 이 이벤트의 뜻을 바꿨다 ──────────────────────────
 *
 * v7 에서 `purchase_completed` 는 "구매가 끝났다" 였다. 실제 결제가 없었으므로 그것은
 * 애초에 대역이었고, B10 이 **진짜 결제가 일어나는 곳**을 만들면서 이름과 사실이 갈렸다 —
 * 결제는 외부 판매처에서 일어나고 우리는 그 결과를 알지 못한다.
 *
 * 그래서 이 이벤트의 의미는 **"데모용 완료 화면에 도달했다"** 로 개정됐다
 * (`docs/13` §2.3). 전환의 마지막 관측점은 이제 이것이 아니라 `external_checkout_opened`
 * 이고, 그 자리가 `checkout.ts` 다. 이 화면은 시연에서 흐름을 닫아 주는 대역으로 남는다.
 *
 * **판매처를 새 탭으로 연 뒤에만 닿는다** (`ProductPage` 의 `[구매를 마쳤습니다]`).
 * 나가지도 않은 사람에게 완료를 보여 주면 그것은 대역이 아니라 거짓이다.
 *
 * ── 왜 `matchId` 를 화면이 들고 다니지 않는가 ────────────────
 *
 * 이 화면은 Deep Link 로 들어온 것이 아니라 앱 안에서 이동해 온 것이라 URL 에
 * `match_id` 가 없다. 그래서 SDK 가 **마지막 유입 컨텍스트를 기억하고 있다가** 이벤트에
 * 붙인다(`Tv2mSdk.context`). 화면이 값을 나르면 경로가 늘 때마다 나를 것이 늘어난다.
 */
export function PurchaseDonePage({
  sdk,
  context,
  onGoHome,
}: {
  sdk: Tv2mSdk | null;
  context: Tv2mLinkContext | null;
  onGoHome: () => void;
}) {
  const t = useT();
  // **한 번만 보고한다.** `StrictMode` 가 효과를 두 번 돌리고, 그대로 두면 구매 한 건이
  // 두 건으로 세어진다 — 전환을 재는 것이 목적인 이벤트에서 그것은 결함이다.
  const reported = useRef(false);

  useEffect(() => {
    if (sdk === null || reported.current) return;
    reported.current = true;
    sdk.trackEvent('purchase_completed');
    // 떠날 때를 기다리지 않고 지금 보낸다. 구매 완료는 이 세션의 마지막 사건이고,
    // 여기서 큐에 남으면 사용자가 앱을 닫는 순간 사라질 수 있다.
    void sdk.flushEvents();
  }, [sdk]);

  return (
    <>
      <div className="card">
        <h2 className="card__title">{t('done.title')}</h2>
        <p className="card__text">{t('done.text')}</p>
        {context !== null && (
          <p className="card__text">
            {t('product.idLabel')} {context.productId}
          </p>
        )}
      </div>

      <div className="badge-row">
        <span className="badge badge--ok">{t('done.recorded')}</span>
      </div>

      <div className="spacer" />

      <button type="button" className="button button--secondary" onClick={onGoHome}>
        {t('done.home')}
      </button>
    </>
  );
}
